import { Spacing } from '@persona-id/design-tokens';
import { ReactNode, useEffect } from 'react';
import styled from 'styled-components';

import LoadQueryTemplatePopover from 'dashboard/components/AnalyticsQueryContainer/LoadQueryTemplatePopover';
import Divider from 'dashboard/components/Divider';
import Tooltip from 'dashboard/components/Tooltip';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { FontWeight } from 'design-system/variables/text';

const SearchAndFilterCardBody = styled(Card.Body)`
  grid-gap: ${Spacing[3]};
`;

const SearchAndFilterCardTitle = styled(Card.Title)<{ $isBold?: boolean }>`
  font-weight: ${({ $isBold }) => ($isBold ? FontWeight.Bold : FontWeight.Regular)};
  font-size: ${({ theme }) => theme.text.paragraph.lgBold.fontSize};
  color: ${({ theme }) => theme.color.text.secondary};
  &:first-child {
    margin-bottom: 0;
  }
`;

const TooltipContainer = styled('div')`
  max-width: 500px;
`;

const ContentContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacing[3]} -${Spacing[5]};
`;

const RunContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing[4]};
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
`;

const QueryTemplateActionContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing[2]};
`;

const QueryTemplateActionButton = styled(Button)``;

const LabelContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  gap: ${Spacing[2]};
`;

const GroupByContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-row-start: 2;
  width: auto;
  padding: ${Spacing[2]};
`;

export interface QueryFilterContainerProps {
  children: ReactNode;
  recencyLabel: ReactNode;
  queryTemplates: AnalyticsQueryTemplate[];
  selectedQueryTemplate: AnalyticsQueryTemplate | null;
  groupByLabel?: string;
  groupByValue?: boolean;
  loading: boolean;
  isQueryTemplateModified: boolean;
  useGroupByToggle?: boolean;
  groupByOnChange?: (checked: boolean) => void;
  onClear: () => void;
  onLoad: (template: AnalyticsQueryTemplate) => void;
  onRun: () => Promise<void>;
  onSave: () => void;
  unsetSelectedTemplate: () => void;
}
const QueryFilterContainer = ({
  children,
  recencyLabel,
  queryTemplates,
  selectedQueryTemplate,
  groupByLabel,
  groupByValue,
  loading,
  isQueryTemplateModified,
  useGroupByToggle = false,
  groupByOnChange,
  onClear,
  onLoad,
  onRun,
  onSave,
  unsetSelectedTemplate,
}: QueryFilterContainerProps) => {
  useEffect(() => {
    // if the selected query template is no longer in the queryTemplates list, then clear the selection
    if (
      selectedQueryTemplate &&
      !queryTemplates.find((template) => template.id === selectedQueryTemplate.id)
    ) {
      unsetSelectedTemplate();
    }
  }, [selectedQueryTemplate, queryTemplates, unsetSelectedTemplate]);

  const maybeQueryTemplateName = selectedQueryTemplate?.attributes.name ?? 'Unsaved Query';
  const groupByAccountLabel = (
    <>
      {groupByLabel}
      <HelpIcon popoverWidth={180}>Dedupes inquiries associated with the same account.</HelpIcon>
    </>
  );

  return (
    <Card>
      <SearchAndFilterCardBody>
        <ContentContainer>
          <LabelContainer>
            <SearchAndFilterCardTitle $isBold={selectedQueryTemplate != null}>
              {maybeQueryTemplateName}
            </SearchAndFilterCardTitle>
            {isQueryTemplateModified && <Sticker variant='yellow'>Modified</Sticker>}
          </LabelContainer>
          <QueryTemplateActionContainer>
            <LoadQueryTemplatePopover
              queryTemplates={queryTemplates}
              loading={loading}
              loadAction={onLoad}
            />
            <Tooltip
              placement='top'
              trigger='hover'
              tooltip={
                <TooltipContainer>
                  Save frequently used filters to enable easier querying. Select from your list of
                  saved queries to quickly load and run reports for a given time interval.
                </TooltipContainer>
              }
              hideArrow={false}
            >
              <QueryTemplateActionButton type='button' variant='basic' onClick={onSave}>
                Save
              </QueryTemplateActionButton>
            </Tooltip>
            <QueryTemplateActionButton
              id='metrics-clear__filters'
              variant='basic'
              onClick={onClear}
            >
              Reset
            </QueryTemplateActionButton>
          </QueryTemplateActionContainer>
        </ContentContainer>
        <StyledDivider />
        {children}
        <StyledDivider />
        <ContentContainer>
          <RunContainer>
            <RunButton
              id='metrics-view__run'
              data-test='metrics-view__run'
              onClick={onRun}
              isPending={loading}
              disabled={loading}
              size='sm'
              width='auto'
            >
              Run Query
            </RunButton>
            {recencyLabel}
          </RunContainer>
          {useGroupByToggle && (
            <GroupByContainer>
              <ToggleSlider
                id={`metrics__group-by-toggle-${groupByLabel}`}
                data-test={`metrics__group-by-toggle-${groupByLabel}`}
                disabled={loading}
                labelOff={groupByAccountLabel}
                labelOn={groupByAccountLabel}
                onChange={groupByOnChange}
                value={groupByValue}
              />
            </GroupByContainer>
          )}
        </ContentContainer>
      </SearchAndFilterCardBody>
    </Card>
  );
};

export default QueryFilterContainer;
